/**
 * Progress Indicator Component
 *
 * Shows wizard progress with step indicators and completion percentage
 */

import React from 'react';
import PropTypes from 'prop-types';
import './ProgressIndicator.css';

const ProgressIndicator = ({ steps, currentStep, completionPercentage, onStepClick, visibleStepIndices }) => {
  return (
    <div className="progress-indicator">
      {/* Completion Percentage */}
      <div className="progress-indicator__percentage">
        <div className="progress-indicator__percentage-bar">
          <div
            className="progress-indicator__percentage-fill"
            style={{ width: `${completionPercentage}%` }}
          />
        </div>
        <span className="progress-indicator__percentage-text">
          {completionPercentage}% Complete
        </span>
      </div>

      {/* Step Indicators */}
      <div className="progress-indicator__steps">
        {steps.map((step, index) => {
          const isVisible = !visibleStepIndices || visibleStepIndices.has(index);
          const isClickable = isVisible && index !== currentStep && !!onStepClick;
          return (
            <div
              key={index}
              className={`progress-indicator__step ${
                index === currentStep ? 'progress-indicator__step--active' : ''
              } ${index < currentStep ? 'progress-indicator__step--completed' : ''} ${
                isClickable ? 'progress-indicator__step--clickable' : ''
              }`}
              onClick={isClickable ? () => onStepClick(index) : undefined}
              role={isClickable ? 'button' : undefined}
              tabIndex={isClickable ? 0 : undefined}
              onKeyDown={isClickable ? (e) => { if (e.key === 'Enter' || e.key === ' ') onStepClick(index); } : undefined}
              aria-label={isClickable ? `Go to ${step.title}` : undefined}
            >
              <div className="progress-indicator__step-circle">
                {index < currentStep ? (
                  <svg width="16" height="16" viewBox="0 0 16 16" fill="currentColor">
                    <path
                      fillRule="evenodd"
                      d="M13.854 3.646a.5.5 0 010 .708l-7 7a.5.5 0 01-.708 0l-3.5-3.5a.5.5 0 11.708-.708L6.5 10.293l6.646-6.647a.5.5 0 01.708 0z"
                      clipRule="evenodd"
                    />
                  </svg>
                ) : (
                  <span>{index + 1}</span>
                )}
              </div>
              <div className="progress-indicator__step-label">{step.title}</div>
              {index < steps.length - 1 && (
                <div className="progress-indicator__step-line" />
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
};

ProgressIndicator.propTypes = {
  steps: PropTypes.arrayOf(
    PropTypes.shape({
      title: PropTypes.string.isRequired,
    })
  ).isRequired,
  currentStep: PropTypes.number.isRequired,
  completionPercentage: PropTypes.number.isRequired,
  onStepClick: PropTypes.func,
  visibleStepIndices: PropTypes.instanceOf(Set),
};

export default ProgressIndicator;
